import { useCallback, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, Upload } from 'lucide-react';
import {
  participantsApi,
  MagicLinkStudent,
  MagicLinkSessionStatus,
  SendAllProgress,
  SendAllResult,
  EMAIL_SEND_INTERVAL_MS,
} from '@/api/endpoints/participants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { useDebounce } from '@/hooks/useDebounce';

// ─── Send-all modal (confirm + progress) ────────────────────────────────────

interface SendAllModalProps {
  totalToSend: number;
  stage: number;
  term: number;
  stageLabel: string;
  onClose: () => void;
  onComplete: (result: SendAllResult) => void;
}

const SendAllModal = ({ totalToSend, stage, term, stageLabel, onClose, onComplete }: SendAllModalProps) => {
  const [phase, setPhase] = useState<'confirm' | 'sending'>('confirm');
  const [progress, setProgress] = useState<SendAllProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleConfirm = useCallback(async () => {
    setPhase('sending');
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await participantsApi.sendAllMagicLinksStream(
        stage,
        term,
        (p) => setProgress(p),
        controller.signal,
      );
      onComplete(result);
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        toast.error('Wysyłanie anulowane');
        onClose();
        return;
      }
      toast.error('Błąd podczas wysyłania emaili');
      onClose();
    }
  }, [stage, term, onComplete, onClose]);

  const handleCancel = () => {
    if (phase === 'sending') {
      abortRef.current?.abort();
    } else {
      onClose();
    }
  };

  const remaining =
    progress != null ? Math.max(0, progress.total - progress.processed) : totalToSend;
  const etaSec = Math.ceil((remaining * EMAIL_SEND_INTERVAL_MS) / 1000);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={phase === 'confirm' ? handleCancel : undefined}
      />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        {phase === 'confirm' ? (
          <>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Wysłać emaile do wszystkich?</h3>
            <p className="mb-2 text-sm text-gray-600">
              Zostanie wysłanych <strong>{totalToSend}</strong> emaili z magic linkami ({stageLabel})
              do uczniów, którzy jeszcze nie otrzymali wiadomości.
            </p>
            <p className="mb-6 text-xs text-gray-500">
              Tempo: 1 email co {EMAIL_SEND_INTERVAL_MS / 1000} s
              {totalToSend > 0 && (
                <> · szacowany czas: ~{Math.ceil((totalToSend * EMAIL_SEND_INTERVAL_MS) / 1000)} s</>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={handleCancel}>Anuluj</Button>
              <Button onClick={() => void handleConfirm()}>
                Wyślij {totalToSend} emaili
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Wysyłanie emaili…</h3>

            <div className="mb-2 h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-primary-600 transition-all duration-300"
                style={{ width: `${progress?.percent ?? 0}%` }}
                role="progressbar"
                aria-valuenow={progress?.percent ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>

            <div className="mb-4 flex items-center justify-between text-sm text-gray-600">
              <span>{progress?.percent ?? 0}%</span>
              <span>
                {progress?.processed ?? 0} / {progress?.total ?? totalToSend}
                {remaining > 0 && <> · ~{etaSec} s</>}
              </span>
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-lg bg-green-50 px-2 py-2">
                <p className="font-semibold text-green-700">{progress?.sent ?? 0}</p>
                <p className="text-green-600">Wysłano</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-2">
                <p className="font-semibold text-gray-700">{progress?.skipped ?? 0}</p>
                <p className="text-gray-500">Pominięto</p>
              </div>
              <div className="rounded-lg bg-red-50 px-2 py-2">
                <p className="font-semibold text-red-700">{progress?.failed ?? 0}</p>
                <p className="text-red-600">Błędy</p>
              </div>
            </div>

            {progress?.currentEmail && (
              <p className="mb-4 truncate text-xs text-gray-500">
                Ostatni: <span className="font-medium text-gray-700">{progress.currentEmail}</span>
              </p>
            )}

            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <LoadingSpinner size="sm" />
                Trwa wysyłanie…
              </span>
              <Button variant="secondary" size="sm" onClick={handleCancel}>
                Anuluj
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ─── Status badges ──────────────────────────────────────────────────────────

const SESSION_STATUS_LABELS: Record<
  MagicLinkSessionStatus,
  { label: string; dot: string; bg: string; text: string }
> = {
  NOT_STARTED: {
    label: 'Nie rozpoczął',
    dot: 'bg-blue-500',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
  },
  IN_PROGRESS: {
    label: 'W trakcie',
    dot: 'bg-amber-500',
    bg: 'bg-amber-100',
    text: 'text-amber-800',
  },
  COMPLETED: {
    label: 'Zakończony',
    dot: 'bg-gray-500',
    bg: 'bg-gray-100',
    text: 'text-gray-600',
  },
};

const SessionStatusBadge = ({
  sessionStatus,
}: {
  sessionStatus: MagicLinkSessionStatus | null;
}) => {
  if (!sessionStatus) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
        Brak
      </span>
    );
  }

  const { label, dot, bg, text } = SESSION_STATUS_LABELS[sessionStatus];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${bg} ${text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
};

const EmailBadge = ({ sentAt }: { sentAt: string | null }) =>
  sentAt ? (
    <span className="text-xs text-gray-700">
      {new Date(sentAt).toLocaleString('pl-PL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })}
    </span>
  ) : (
    <span className="text-xs text-gray-400">Nie wysłano</span>
  );

function buildMagicLinkUrl(token: string): string {
  return `${window.location.origin}/weryfikacja?token=${token}`;
}

async function copyMagicLink(token: string): Promise<void> {
  const url = buildMagicLinkUrl(token);
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = url;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

const StatCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div className="rounded-lg border bg-white p-4">
    <p className="text-sm text-gray-500">{label}</p>
    <p className={`mt-1 text-3xl font-bold ${color}`}>{value}</p>
  </div>
);

// ─── Stage tab button ───────────────────────────────────────────────────────

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
      active
        ? 'border-primary-600 text-primary-700 bg-white'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    {children}
  </button>
);

// ─── Term-2 import modal ────────────────────────────────────────────────────

interface Term2ImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const Term2ImportModal = ({ onClose, onSuccess }: Term2ImportModalProps) => {
  const [raw, setRaw] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: (emails: string[]) => participantsApi.addStudentsToTerm2(emails),
    onSuccess: (result) => {
      const parts: string[] = [`Dodano: ${result.added}`];
      if (result.alreadyExists > 0) parts.push(`już istnieje: ${result.alreadyExists}`);
      if (result.notFound.length > 0) {
        parts.push(`nie znaleziono: ${result.notFound.length}`);
        setErrors(result.notFound);
      } else {
        setErrors([]);
      }
      toast.success(parts.join(' · '));
      if (result.added > 0) onSuccess();
    },
    onError: () => toast.error('Błąd importu'),
  });

  const parseEmails = (text: string): string[] => {
    text = text.trim();
    if (!text) return [];
    // Try JSON array of strings or objects with "email" key
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((item: unknown) => {
          if (typeof item === 'string') return item.trim();
          if (item && typeof item === 'object' && 'email' in item) return String((item as Record<string,unknown>).email).trim();
          return '';
        }).filter(Boolean);
      }
    } catch {
      // fallback: one email per line or comma separated
    }
    return text
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter((e) => e.includes('@'));
  };

  const handleImport = () => {
    const emails = parseEmails(raw);
    if (emails.length === 0) {
      toast.error('Brak prawidłowych adresów email');
      return;
    }
    setErrors([]);
    mutation.mutate(emails);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={mutation.isPending ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Importuj uczniów do Etapu 1 — Termin 2</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Wklej listę emaili uczniów. Obsługiwane formaty:
            </p>
          </div>
          <button onClick={onClose} disabled={mutation.isPending} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-md bg-gray-50 border p-3 text-xs text-gray-600 space-y-1.5 font-mono">
            <p className="font-semibold text-gray-700 font-sans text-[11px] uppercase tracking-wide">Przykłady</p>
            <p>{"[\"jan@szkola.pl\", \"anna@szkola.pl\"]"}</p>
            <p>{"[{\"email\": \"jan@szkola.pl\"}, {\"email\": \"anna@szkola.pl\"}]"}</p>
            <p>jan@szkola.pl, anna@szkola.pl</p>
            <p>jan@szkola.pl<br/>anna@szkola.pl</p>
          </div>

          <textarea
            className="w-full h-48 rounded-md border border-gray-300 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            placeholder='["jan@szkola.pl", "anna@szkola.pl"]'
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            disabled={mutation.isPending}
          />

          {errors.length > 0 && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-xs font-semibold text-red-700 mb-1">Nie znaleziono {errors.length} email(i):</p>
              <ul className="text-xs text-red-600 space-y-0.5 max-h-32 overflow-y-auto">
                {errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-between items-center gap-3">
          <p className="text-xs text-gray-500">
            Znalezionych: <strong>{parseEmails(raw).length}</strong>
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Anuluj</Button>
            <Button onClick={handleImport} disabled={mutation.isPending || !raw.trim()}>
              {mutation.isPending ? (
                <span className="flex items-center gap-2"><LoadingSpinner size="sm" />Importuję...</span>
              ) : (
                <span className="flex items-center gap-2"><Upload className="h-4 w-4" />Importuj</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Tab config ─────────────────────────────────────────────────────────────

type MagicLinkTab = {
  id: string;
  stage: 1 | 2;
  term: 1 | 2;
  label: string;
  shortLabel: string;
  description?: string;
};

const MAGIC_LINK_TABS: MagicLinkTab[] = [
  {
    id: 'e1t1',
    stage: 1,
    term: 1,
    label: 'Etap 1 — Termin 1',
    shortLabel: 'Etap 1, Termin 1',
    description:
      'Wszyscy uczestnicy poza zapisanymi do Terminu 2 — także ci bez ukończonego testu. Status pokazuje postęp (nie rozpoczął / w trakcie / zakończony).',
  },
  {
    id: 'e1t2',
    stage: 1,
    term: 2,
    label: 'Etap 1 — Termin 2',
    shortLabel: 'Etap 1, Termin 2',
    description: 'Drugi termin Etapu 1. Wygeneruj tokeny i wyślij emaile z informacją o Terminie 2.',
  },
  {
    id: 'e2',
    stage: 2,
    term: 1,
    label: 'Etap 2 — Ogólnopolski',
    shortLabel: 'Etap 2',
    description: 'Kwalifikacja uczniów odbywa się w sekcji Kwalifikacja do Etapu 2. Tutaj możesz wygenerować tokeny dla zakwalifikowanych i wysłać im emaile.',
  },
];

// ─── Main page ──────────────────────────────────────────────────────────────

export const MagicLinksPage = () => {
  const queryClient = useQueryClient();

  const [activeTabId, setActiveTabId] = useState<string>('e1t1');
  const activeTab = MAGIC_LINK_TABS.find((t) => t.id === activeTabId) ?? MAGIC_LINK_TABS[0];
  const { stage, term } = activeTab;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [showSendAll, setShowSendAll] = useState(false);
  const [showTerm2Import, setShowTerm2Import] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [tokenActionId, setTokenActionId] = useState<string | null>(null);

  // Reset page when tab or search changes
  const handleTabChange = (tabId: string) => {
    setActiveTabId(tabId);
    setPage(1);
    setSearch('');
  };

  const { data, isLoading } = useQuery({
    queryKey: ['magic-links', { page, search: debouncedSearch, stage, term }],
    queryFn: () =>
      participantsApi.getMagicLinkStatus({
        page,
        limit: 20,
        search: debouncedSearch || undefined,
        stage,
        term,
      }),
  });

  const students = data?.data ?? [];
  const meta = data?.meta;
  const stats = data?.stats;
  const toSendCount = (stats?.totalTokens ?? 0) - (stats?.totalEmailed ?? 0);

  const generateMutation = useMutation({
    mutationFn: () => participantsApi.generateTokens(stage, term),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      toast.success(`Wygenerowano ${result.created} tokenów (pominięto ${result.skipped})`);
    },
    onError: () => toast.error('Błąd podczas generowania tokenów'),
  });

  const handleSendAllComplete = useCallback(
    (result: SendAllResult) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      setShowSendAll(false);
      if (result.failed > 0) {
        toast.error(`Wysłano ${result.sent}, błędy: ${result.failed}`);
      } else {
        toast.success(`Wysłano ${result.sent} emaili pomyślnie`);
      }
    },
    [queryClient],
  );

  const sendOneMutation = useMutation({
    mutationFn: (studentId: string) => participantsApi.sendMagicLink(studentId, stage, term),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      setSendingId(null);
      toast.success(`Email wysłany do ${result.email}`);
    },
    onError: () => {
      setSendingId(null);
      toast.error('Błąd podczas wysyłania emaila');
    },
  });

  const generateTokenMutation = useMutation({
    mutationFn: (studentId: string) => participantsApi.generateTokenForStudent(studentId, stage, term),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      setTokenActionId(null);
      toast.success(
        result.action === 'created'
          ? 'Token utworzony'
          : 'Wygenerowano nowy token (stary link przestanie działać)',
      );
    },
    onError: () => {
      setTokenActionId(null);
      toast.error('Błąd podczas generowania tokenu');
    },
  });

  const handleSendOne = (student: MagicLinkStudent) => {
    setSendingId(student.id);
    sendOneMutation.mutate(student.id);
  };

  const handleTokenAction = (student: MagicLinkStudent) => {
    if (student.token) {
      const confirmed = window.confirm(
        'Stary link przestanie działać. Kontynuować?',
      );
      if (!confirmed) return;
    }
    setTokenActionId(student.id);
    generateTokenMutation.mutate(student.id);
  };

  const handleCopyMagicLink = async (student: MagicLinkStudent) => {
    if (!student.token) return;
    try {
      await copyMagicLink(student.token);
      toast.success(`Skopiowano link dla ${student.firstName} ${student.lastName}`);
    } catch {
      toast.error('Nie udało się skopiować linku');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Magic Linki</h2>
        <p className="mt-1 text-sm text-gray-500">
          Generowanie i wysyłanie jednorazowych linków dostępowych dla uczestników
        </p>
      </div>

      {/* Stage tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1">
          {MAGIC_LINK_TABS.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTabId === tab.id}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </div>
      </div>

      {/* Tab info callout */}
      {activeTab.description && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-blue-900">{activeTab.label}</p>
            <p className="mt-0.5 text-xs text-blue-700">{activeTab.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {stage === 1 && term === 2 && (
            <Button
              variant="secondary"
              onClick={() => setShowTerm2Import(true)}
            >
              <span className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Importuj uczniów do Terminu 2
              </span>
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? (
              <span className="flex items-center gap-2"><LoadingSpinner size="sm" />Generowanie...</span>
            ) : (
              stage === 2 ? 'Generuj tokeny dla zakwalifikowanych' : 'Generuj tokeny'
            )}
          </Button>
          <Button
            onClick={() => setShowSendAll(true)}
            disabled={toSendCount === 0 || showSendAll}
          >
            Wyślij do wszystkich ({toSendCount})
          </Button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2">
          <Input
            placeholder="Szukaj uczestnika..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-64"
          />
          {search && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setPage(1); }}>
              Wyczyść
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label={stage === 2 ? 'Zakwalifikowani' : 'Wszyscy uczestnicy'}
            value={stats.totalStudents}
            color="text-gray-900"
          />
          <StatCard label="Tokeny wygenerowane" value={stats.totalTokens} color="text-blue-600" />
          <StatCard label="Emaile wysłane" value={stats.totalEmailed} color="text-green-600" />
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Lista uczestników — {activeTab.shortLabel}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-10" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Imię i nazwisko</TableHead>
                  <TableHead>Szkoła</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status testu</TableHead>
                  <TableHead>Wysłano email</TableHead>
                  <TableHead className="text-right">Akcja</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-gray-500">
                      {stage === 2 && !search
                        ? 'Brak uczniów zakwalifikowanych do Etapu 2. Skorzystaj z kwalifikacji w sekcji Kwalifikacja do Etapu 2.'
                        : stage === 1 && term === 2 && !search
                        ? 'Brak uczestników w Terminie 2. Wygeneruj tokeny, aby rozpocząć.'
                        : search
                        ? 'Brak wyników dla podanej frazy.'
                        : 'Brak uczestników.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student, idx) => {
                    const isSending = sendingId === student.id;
                    const isTokenAction = tokenActionId === student.id;
                    const rowNum = (meta ? (meta.page - 1) * meta.limit : 0) + idx + 1;
                    return (
                      <TableRow key={student.id}>
                        <TableCell className="text-gray-400 text-sm">{rowNum}</TableCell>
                        <TableCell className="font-medium">
                          {student.firstName} {student.lastName}
                        </TableCell>
                        <TableCell>
                          {student.school ? (
                            <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                              {student.school.name}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-gray-600 text-sm">{student.email}</TableCell>
                        <TableCell><SessionStatusBadge sessionStatus={student.sessionStatus} /></TableCell>
                        <TableCell><EmailBadge sentAt={student.emailSentAt} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleTokenAction(student)}
                              disabled={isTokenAction || isSending}
                            >
                              {isTokenAction ? (
                                <span className="flex items-center gap-1.5">
                                  <LoadingSpinner size="sm" />
                                  {student.token ? 'Generowanie...' : 'Tworzenie...'}
                                </span>
                              ) : student.token ? (
                                'Nowy token'
                              ) : (
                                'Utwórz token'
                              )}
                            </Button>
                            {student.token ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => void handleCopyMagicLink(student)}
                                  disabled={isSending || isTokenAction}
                                  title="Kopiuj magic link"
                                >
                                  <span className="flex items-center gap-1.5">
                                    <Copy className="h-3.5 w-3.5" />
                                    Kopiuj link
                                  </span>
                                </Button>
                                <Button
                                  size="sm"
                                  variant={student.emailSentAt ? 'secondary' : 'primary'}
                                  onClick={() => handleSendOne(student)}
                                  disabled={isSending || isTokenAction}
                                >
                                  {isSending ? (
                                    <span className="flex items-center gap-1.5">
                                      <LoadingSpinner size="sm" />Wysyłanie...
                                    </span>
                                  ) : student.emailSentAt ? 'Wyślij ponownie' : 'Wyślij'}
                                </Button>
                              </>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Strona {meta.page} z {meta.totalPages}</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Poprzednia
            </Button>
            <Button variant="secondary" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
              Następna
            </Button>
          </div>
        </div>
      )}

      {/* Send-all modal */}
      {showSendAll && (
        <SendAllModal
          totalToSend={toSendCount}
          stage={stage}
          term={term}
          stageLabel={activeTab.shortLabel}
          onClose={() => setShowSendAll(false)}
          onComplete={handleSendAllComplete}
        />
      )}

      {/* Term-2 import modal */}
      {showTerm2Import && (
        <Term2ImportModal
          onClose={() => setShowTerm2Import(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['magic-links'] });
            setShowTerm2Import(false);
          }}
        />
      )}

    </div>
  );
};
